import { FindingChange, FindingService, ReviewService } from '@analysis/domain';
import { type FindingRepository } from '../../ports';
import { Finding, FindingParameters, StepTask, UuidGenerator } from '@telespot/domain';

export interface SyncReviewedFindingChangesPayload {
  sampleId: string;
  currentAnalystId: string;
  sourceAnalystId: string;
  changedFindings: Map<string, FindingChange>;
  newFindings: FindingParameters[];
}

export interface SyncReviewedFindingChangesResult {
  savedFindings: Finding[];
}

export class SyncReviewedFindingChangesUseCase {
  private readonly FINDING_TYPES = [StepTask.TEXT, StepTask.CLASSIFICATION];

  constructor(
    private readonly findings: FindingRepository,
    private readonly uuids: UuidGenerator
  ) {}

  public async execute({
    sampleId,
    currentAnalystId,
    sourceAnalystId,
    changedFindings,
    newFindings,
  }: SyncReviewedFindingChangesPayload): Promise<SyncReviewedFindingChangesResult> {
    if (sourceAnalystId === currentAnalystId) return { savedFindings: [] };

    const sourceAnalystFindings = await this.findings.listForAnalystInSample({
      sampleId,
      analystId: sourceAnalystId,
      findingTypes: this.FINDING_TYPES,
    });

    const currentAnalystFindings = await this.findings.listForAnalystInSample({
      sampleId,
      analystId: currentAnalystId,
      findingTypes: this.FINDING_TYPES,
    });

    const changedFindingUuids = Array.from(changedFindings.keys()); // Keys are now uuids

    const sourceAnalystFindingUuids = sourceAnalystFindings
      .filter((finding) => changedFindingUuids.includes(finding.uuid))
      .map((finding) => finding.uuid);

    const changedLatestFindings =
      sourceAnalystFindingUuids.length > 0 ? await this.findings.fetchLatestByIds(sourceAnalystFindingUuids) : [];

    const findingsWithChanges = changedLatestFindings.map((finding) => {
      const change = changedFindings.get(finding.uuid);
      return change ? FindingService.applyChange(finding, change) : finding;
    });
    const sourceAnalystChangedFindings = findingsWithChanges.filter((finding) => finding.creatorId === sourceAnalystId);

    const sourceNewFindings = newFindings.filter((finding) => finding.creatorId === sourceAnalystId);

    const newFindingsWithChanges = sourceNewFindings.map((finding) => {
      const change = changedFindings.get(finding.uuid);
      return change ? FindingService.applyChange(new Finding(finding), change) : finding;
    });

    const findingsToSave: Finding[] = [];

    for (const newFinding of sourceNewFindings) {
      const changedSourceNewFinding = newFindingsWithChanges.find((f) => f.uuid === newFinding.uuid);
      const findingToUse = changedSourceNewFinding || newFinding;

      const currentFinding = currentAnalystFindings.find(
        (f) => f.assetId === newFinding.assetId && f.pipelineStepId === newFinding.pipelineStepId
      );

      const copiedFinding = this.copyFinding(findingToUse, currentAnalystId, currentFinding);

      findingsToSave.push(copiedFinding);
    }

    for (const sourceFinding of sourceAnalystFindings) {
      const changedSourceFinding = sourceAnalystChangedFindings.find((f) => f.uuid === sourceFinding.uuid);
      const findingToUse = changedSourceFinding || sourceFinding;

      const currentFinding = currentAnalystFindings.find(
        (f) => f.assetId === sourceFinding.assetId && f.pipelineStepId === sourceFinding.pipelineStepId
      );

      const copiedFinding = this.copyFinding(findingToUse, currentAnalystId, currentFinding);

      findingsToSave.push(copiedFinding);
    }

    const findingsToSaveUuids = findingsToSave.map((f) => f.uuid);

    const findingsToEmpty = currentAnalystFindings.filter((f) => !findingsToSaveUuids.includes(f.uuid));
    const emptyFindings = ReviewService.createEmptyFindings(findingsToEmpty);
    findingsToSave.push(...emptyFindings);

    const savedFindings = await this.findings.saveAll(findingsToSave, sampleId);

    return { savedFindings };
  }

  private copyFinding(sourceFinding: any, currentAnalystId: string, currentFinding?: any): Finding {
    const isNewFinding = sourceFinding.id === undefined;
    return new Finding({
      id: undefined,
      uuid: currentFinding?.uuid ?? this.uuids.generate(),
      assetId: sourceFinding.assetId,
      analysisId: currentFinding?.analysisId,
      pipelineStepId: sourceFinding.pipelineStepId,
      type: sourceFinding.type,
      version: currentFinding?.version ?? 1,
      creatorId: currentAnalystId,
      creatorEntity: 'user',
      originId: isNewFinding ? undefined : sourceFinding.id,
      pipelineId: sourceFinding.pipelineId,
      data: sourceFinding.data,
      createdAt: new Date(),
      updatedAt: new Date(),
      deletedAt: undefined,
    });
  }
}
